import { SubmitFormDialog } from '@shared/components/dialogs/submit-form-dialog';
import { RefObject } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import BasicDateTimePicker from '@shared/components/BasicDateTimePicker';
import SelectField, { Identifiable } from '@shared/components/SelectField';
import TextField from '@shared/components/TextField';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { useSubscriptionPlan } from '@shared/store';
import { SubscriptionPlansEnum } from '../../../../subscription/entity';
import ServicesAutoComplete from '../../../../service/components/ServicesAutoComplete';
import type { Service } from '../../../../service/entity';
import { useServicesQuery } from '../../../../service/queries';
import { depositStatusOptions, VisitDetail, type VisitForm } from '../../../entity';
import { useUpdateVisitMutation } from '../../../queries';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

type FormFieldConfig = {
  type: 'select' | 'text' | 'number' | 'datetime' | 'autocomplete';
  fieldPath: keyof VisitForm;
  label: string;
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
  multiline?: boolean;
  shouldDisplay: boolean;
  minRows?: number;
  maxRows?: number;
  items?: Identifiable[];
  keyExtractor?: (item: Identifiable) => string | number;
  labelExtractor?: (item: Identifiable) => string;
  queryData?: Service[];
  isLoading?: boolean;
  condition?: boolean;
};

const VisitDetailEditDialog = (props: {
  dialogRef: RefObject<ImperativeFormDialogRefType | null>;
  visit: VisitDetail;
  methods: UseFormReturn<VisitForm>;
}) => {
  const { dialogRef, visit, methods } = props;
  const { t } = useIntlTranslations();
  const { visitId, clientId } = useParams();
  const { data: services, isLoading: isLoadingServices } = useServicesQuery();
  const { mutate: updateVisitMutation } = useUpdateVisitMutation(visitId, clientId);
  const plan = useSubscriptionPlan();
  const isExpertPlan = plan === SubscriptionPlansEnum.EXPERT;

  const visitClosed = useWatch({ control: methods.control, name: 'visitClosed' });

  const onSubmit = () => {
    const formData = methods.getValues();

    updateVisitMutation(
      { ...formData, visitServiceId: visit.visitServiceId },
      {
        onSuccess: () => {
          methods.reset(formData);
          close();
        },
      },
    );
  };

  const availablePoints = t('editVisitDetailDialog.availablePoints', {
    points: visit.client.loyaltyPoints || 0,
  });

  const formFieldsConfig = getEditVisitFormFields(
    visit,
    visitClosed,
    services,
    isLoadingServices,
    isExpertPlan,
    availablePoints,
  );

  return (
    <SubmitFormDialog
      methods={methods}
      ref={dialogRef}
      onSubmit={() => onSubmit()}
      title={t('editVisitDetailDialog.visitDetail')}
    >
      {handleInputRef => (
        <>
          {formFieldsConfig.map((field, index) => {
            if (!field.shouldDisplay) {
              return null;
            }

            switch (field.type) {
              case 'select':
                return (
                  <SelectField
                    key={index}
                    disabled={field.disabled}
                    label={t(field.label)}
                    //upravit typ abych mohl vynechat !
                    items={field.items!}
                    inputRef={(el: HTMLInputElement | null) => handleInputRef(field.fieldPath)(el)}
                    control={methods.control}
                    //upravit typ abych mohl vynechat !
                    keyExtractor={field.keyExtractor!}
                    labelExtractor={field.labelExtractor!}
                    fieldPath={field.fieldPath}
                  />
                );
              case 'datetime':
                return (
                  <BasicDateTimePicker
                    key={index}
                    inputRef={(el: HTMLInputElement | null) => handleInputRef(field.fieldPath)(el)}
                    fieldPath={field.fieldPath}
                    control={methods.control}
                    disabled={field.disabled}
                    label={field.label && t(field.label)}
                  />
                );
              case 'autocomplete':
                return (
                  <ServicesAutoComplete
                    key={index}
                    fieldPath={field.fieldPath}
                    control={methods.control}
                    disabled={field.disabled}
                    queryData={field.queryData}
                    isLoading={Boolean(field.isLoading)}
                    inputRef={(el: HTMLInputElement | null) => handleInputRef(field.fieldPath)(el)}
                  />
                );
              case 'text':
              case 'number':
                return (
                  <TextField
                    key={index}
                    disabled={field.disabled}
                    fieldPath={field.fieldPath}
                    label={t(field.label)}
                    helperText={
                      field.helperText &&
                      t(field.helperText, { points: visit.client.loyaltyPoints || 0 })
                    }
                    type={field.type}
                    fullWidth
                    inputRef={(el: HTMLInputElement | null) => handleInputRef(field.fieldPath)(el)}
                    control={methods.control}
                    required={field.required}
                    multiline={field.multiline}
                    minRows={field.minRows}
                    maxRows={field.maxRows}
                  />
                );
              default:
                return null;
            }
          })}
        </>
      )}
    </SubmitFormDialog>
  );
};

export default VisitDetailEditDialog;

const getEditVisitFormFields = (
  visit: VisitDetail,
  visitClosed: boolean,
  services: Service[] | undefined,
  isLoadingServices: boolean,
  isExpertPlan: boolean,
  availablePoints: string,
): FormFieldConfig[] =>
  [
    {
      type: 'select' as const,
      fieldPath: 'depositStatus',
      disabled: visitClosed,
      label: 'editVisitDetailDialog.depositStatus',
      items: depositStatusOptions,
      keyExtractor: (status: Identifiable) => status.id,
      labelExtractor: (status: Identifiable) => status.name,
      shouldDisplay: Boolean(visit.client.deposit),
    },
    {
      type: 'text' as const,
      fieldPath: 'deposit',
      disabled: visitClosed,
      label: 'editVisitDetailDialog.depositAmount',
      helperText: 'editVisitDetailDialog.depositHelperText',
      required: true,
      shouldDisplay: Boolean(visit.client.deposit),
    },
    {
      type: 'datetime' as const,
      fieldPath: 'date',
      disabled: visitClosed,
      shouldDisplay: true,
    },
    {
      type: 'datetime' as const,
      fieldPath: 'dateTo',
      label: 'editVisitDetailDialog.dateTo',
      disabled: visitClosed,
      shouldDisplay: true,
    },
    {
      type: 'autocomplete' as const,
      fieldPath: 'hairCutId',
      disabled: visitClosed,
      queryData: services,
      isLoading: isLoadingServices,
      shouldDisplay: true,
    },
    {
      type: 'number' as const,
      fieldPath: 'paidPrice',
      disabled: visitClosed,
      label: 'editVisitDetailDialog.priceLabel',
      shouldDisplay: true,
      helperText: 'editVisitDetailDialog.priceHelperText',
    },
    {
      type: 'number' as const,
      shouldDisplay: isExpertPlan,
      fieldPath: 'loyaltyPointsUsed',
      disabled: visitClosed || visit.client.loyaltyPoints === 0,
      label: 'editVisitDetailDialog.loyaltyPointsUsed',
      helperText: 'editVisitDetailDialog.availablePoints',
      points: availablePoints,
      // ('editVisitDetailDialog.availablePoints', { points: visit.client.loyaltyPoints || 0 }),
    },
    {
      type: 'text' as const,
      fieldPath: 'note',
      disabled: visitClosed,
      shouldDisplay: true,
      label: 'editVisitDetailDialog.visitInfo',
      multiline: true,
      minRows: 2,
      maxRows: 5,
    },
  ].filter(Boolean) as FormFieldConfig[];
